import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articlesData } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articlesData.find((a) => a.id === articleId) || articlesData[0];

  const relatedArticles = articlesData
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishDate,
            image: article.coverImage,
            publisher: {
              '@type': 'Organization',
              name: '北条麻妃电影全集精选',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hojomaki-film.com/' },
              { '@type': 'ListItem', position: 2, name: '影评文章', item: 'https://hojomaki-film.com/article/1' },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://hojomaki-film.com/article/${article.id}` },
            ],
          }),
        }}
      />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <article>
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '影评文章', href: '/article/1' },
                  { label: article.title },
                ]}
              />

              <div className="mt-4">
                <span className="inline-block bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
                <span className="whitespace-nowrap">
                  <i className="ri-user-line mr-1"></i>
                  {article.author}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-calendar-line mr-1"></i>
                  {article.publishDate}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime}
                </span>
              </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 md:px-6 mb-8">
              <div className="aspect-[16/9] rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 md:px-6 pb-10">
              <div
                className="prose prose-foreground max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
                  prose-strong:text-foreground-900
                  [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:text-base [&_p]:mb-4
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block bg-secondary-100 text-secondary-700 text-sm px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-14 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <span className="inline-block bg-accent-100 text-accent-700 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap mb-2">
                          {ra.category}
                        </span>
                        <h3 className="font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 text-sm">
                          {ra.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500 whitespace-nowrap">
                          {ra.author} · {ra.publishDate}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}